/* eslint-disable react/no-multi-comp */
import classNames from 'classnames';
import { ContentState, convertFromHTML, Editor, EditorState, RichUtils } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import EditorBlockButton from './EditorBlockButton';
import EditorDropdown from './EditorDropdown';

const HEADER_TYPES = [
  { label: 'No Heading', style: 'unstyled' },
  { label: 'Heading 1', style: 'header-one' },
  { label: 'Heading 2', style: 'header-two' },
  { label: 'Heading 3', style: 'header-three' },
  { label: 'Heading 4', style: 'header-four' },
  { label: 'Heading 5', style: 'header-five' },
  { label: 'Heading 6', style: 'header-six' },
];

const BLOCK_TYPES = [
  {
    icon: 'icon icon-text',
    style: 'unordered-list-item',
    title: 'Unordered List',
  },
  { icon: 'icon icon-list', style: 'ordered-list-item', title: 'Ordered List' },
];

const INLINE_TYPES = [
  { label: 'B', style: 'BOLD', decoration: { fontWeight: 'bold' } },
  { label: 'I', style: 'ITALIC', decoration: { fontStyle: 'italic' } },
  {
    label: 'U',
    style: 'UNDERLINE',
    decoration: { textDecoration: 'underline' },
  },
];

export default class RichTextEditor extends Component {
  static propTypes = {
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    initialValue: PropTypes.string,
    enableHeadings: PropTypes.bool,
  };

  static defaultProps = {
    placeholder: '',
    enableHeadings: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      editorState: EditorState.createEmpty(),
    };
  }

  componentDidMount() {
    if (this.props.initialValue) {
      this.applyInitialHtml(this.props.initialValue);
    }
  }

  componentDidUpdate(prevProps) {
    const { value, onChange, initialValue } = this.props;

    if (initialValue !== prevProps.initialValue && typeof initialValue === 'string') {
      this.applyInitialHtml(initialValue);
      return;
    }

    const newValue = stateToHTML(this.state.editorState.getCurrentContent());
    const editorHasContent =
      this.state.editorState.getCurrentContent().getPlainText().trim() !== '';

    if (value === '' || value == null) {
      if (prevProps.value !== value && !editorHasContent) {
        this.setState({
          editorState: EditorState.createEmpty(),
        });
        return;
      }
      // When form value is null but user has typed, sync editor content to form
      if (editorHasContent) {
        onChange(newValue);
      }
      if (prevProps.value === value && prevProps.value !== newValue) {
        onChange(newValue);
      }
      return;
    }
    const inputValueChanged = prevProps.value !== newValue;
    if (inputValueChanged) {
      onChange(newValue);
    }
  }

  applyInitialHtml = (html) => {
    const blocksFromHTML = convertFromHTML(html || '');
    const filteredBlocks = (blocksFromHTML.contentBlocks || []).filter((block) => {
      if (!block) return false;
      const isEmpty = !block.getText().trim();
      const isUnstyled = block.getType && block.getType() === 'unstyled';
      return !(isEmpty && isUnstyled);
    });
    const contentState = ContentState.createFromBlockArray(
      filteredBlocks,
      blocksFromHTML.entityMap
    );
    let nextEditorState;
    try {
      nextEditorState = EditorState.createWithContent(contentState);
    } catch (error) {
      nextEditorState = EditorState.createEmpty();
    }

    this.setState(
      {
        editorState: nextEditorState,
      },
      () => {
        setTimeout(() => this.focus());
      }
    );
  };

  onEditorStateChange = (editorState) => {
    this.setState({
      editorState,
    });
  };

  focus = () => {
    this.editor.focus();
  };

  handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this.onEditorStateChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  toggleBlockType = (blockType) => {
    this.onEditorStateChange(RichUtils.toggleBlockType(this.state.editorState, blockType));
  };

  toggleInlineType = (style) => {
    this.onEditorStateChange(RichUtils.toggleInlineStyle(this.state.editorState, style));
  };

  render() {
    const blockType = this.state.editorState
      .getCurrentContent()
      .getBlockForKey(this.state.editorState.getSelection().getStartKey())
      .getType();

    return (
      <div className="rich-text-editor">
        <div className="editor-toolbar">
          {INLINE_TYPES.map((type) => (
            <button
              type="button"
              key={type.label}
              className={classNames('toolbar-button', 'toolbar-block-button')}
              onClick={() => this.toggleInlineType(type.style)}
              style={type.decoration}
            >
              {type.label}
            </button>
          ))}
          {BLOCK_TYPES.map((type) => (
            <EditorBlockButton
              key={type.icon}
              active={type.style === blockType}
              icon={type.icon}
              title={type.title}
              onToggle={this.toggleBlockType}
              style={type.style}
            />
          ))}
          {this.props.enableHeadings && (
            <>
              <div className="toolbar-separator" />
              <EditorDropdown
                headers={HEADER_TYPES}
                onToggle={this.toggleBlockType}
                active={blockType}
              />
            </>
          )}
        </div>
        <div className="rich-text-editor__input" onMouseDown={this.focus}>
          <Editor
            editorState={this.state.editorState}
            onChange={this.onEditorStateChange}
            handleKeyCommand={this.handleKeyCommand}
            ref={(element) => {
              this.editor = element;
            }}
          />
        </div>
      </div>
    );
  }
}
